import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRaw,
  watch,
  type ComponentPublicInstance,
} from 'vue';
import { cloneDeep } from 'lodash';
import md5 from 'md5';
import { useAcl, useI18n, useMixpanel, useReportForm, useReportUtils, useWorkspace } from '@/composables';
import { $emitter } from '@/services/emitter';
import helpers from '@/services/helpers';
import {
  getFlowApiRequest,
  postCreateFlowApiRequest,
  putUpdateFlowApiRequest,
} from '@/services/api/flows-api';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import {
  populateLandingsStoreCacheApiRequest,
  updateLandingsStoreCacheApiRequest,
} from '@/services/api/api-services/landings';
import {
  getOffersListApiRequest,
} from '@/services/api/offers-api';
import {
  populateOffersStoreCacheApiRequest,
  updateOffersStoreCacheApiRequest,
} from '@/services/api/api-services/offers';
import { getLandingsListApiRequest } from '@/services/api/landings-api';
import { refreshUserProfileFromApi } from '@/services/api/api-services/load-profile';
import formMock from './config/form-mock';
import { useStore } from '@/store';
import { populateRedirectDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import {
  applyFlowSubmitErrorsFromResponse,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';
import type { FieldErrors } from '@/services/models';

export type FlowDialogMode = 'create' | 'edit' | 'duplicate';

export type FlowDialogData = {
  mode?: FlowDialogMode;
  id?: string;
  createFromConvertToFlow?: boolean;
  form?: Record<string, unknown>;
  onConfirm?: (payload: unknown) => void;
};

type FlowForm = ReturnType<typeof formMock> & {
  workspaceId?: string | null;
  countryId?: number;
  name?: string;
  tags?: Array<{ id?: string; name: string } | string>;
  rules: Array<{
    paths: unknown[];
    [key: string]: unknown;
  }>;
};

type FlowNavigationRef = ComponentPublicInstance & {
  openNavigation: () => void;
  selectFlowSettings: () => void;
  setPath: (path: unknown, rule: unknown) => void;
  setRule: (rule: unknown) => void;
  processOfferError: (ruleIndex: number, pathIndex: number, offerIndex: number) => unknown[][];
  fixOfferErrorIndexes: (
    ruleIndex: number,
    pathIndex: number,
    offerIndex: number,
    errors: unknown[][],
  ) => unknown[][];
  processLandingError: (ruleIndex: number, pathIndex: number, landingIndex: number) => unknown[][];
  fixLandingErrorIndexes: (
    ruleIndex: number,
    pathIndex: number,
    landingIndex: number,
    errors: unknown[][],
  ) => unknown[][];
  processPathError: (ruleIndex: number, pathIndex: number) => unknown[][];
  fixPathErrorIndexes: (
    ruleIndex: number,
    pathIndex: number,
    errors: unknown[][],
  ) => unknown[][];
  processRuleError: (ruleIndex: number) => unknown[][];
  fixRuleErrorIndexes: (ruleIndex: number, errors: unknown[][]) => unknown[][];
};

const defaultForm = (): FlowForm => ({
  ...formMock(),
});

const defaultFormErrors = () => ({
  name: false,
  rules: [] as unknown[],
});

export const useFlowDialog = (
  mode: FlowDialogMode,
  getData: () => FlowDialogData,
  emitClose: () => void,
  emitRefresh: () => void,
) => {
  const store = useStore();
  const workspaceApi = useWorkspace();
  const { t } = useI18n();
  const { aclReadOnly } = useAcl();
  const { trackMixpanel } = useMixpanel();
  const reportUtilsApi = useReportUtils({});

  const edit = ref(mode === 'edit');
  const show = ref(false);
  const submitLoading = ref(false);
  const isLoadingDictionary = ref(false);
  const view = ref('settings');
  const active_path = ref<unknown>([]);
  const active_rule = ref<unknown>([]);
  const rule_placeholder = ref('');
  const form = ref<FlowForm>(defaultForm());
  const active_path_errors = ref<Record<string, unknown>>({});
  const form_errors = ref(defaultFormErrors());
  const flow_id = ref('');
  const oldHash = ref('');
  const offerOptions = ref<Offer[]>([]);
  const landingOptions = ref<Landing[]>([]);
  const errorsRaw = ref<unknown[][] | null>(null);
  const errorMessage = ref('');
  const fieldErrors = ref<FieldErrors>({});
  const errorsControlActive = ref(false);
  const flowNavigationRef = ref<FlowNavigationRef | null>(null);

  const reportForm = useReportForm(() => ({
    edit: edit.value,
    mode: edit.value ? 'edit' : 'create',
  }));

  const workspacesList = computed(() => reportForm.workspacesList.value);
  const defaultWorkspace = computed(() => reportForm.defaultWorkspace.value);

  const modalTitle = computed(() => t('Flow'));

  const header = computed(() => {
    if (mode === 'edit') return t('Edit Flow');
    if (mode === 'duplicate') return t('Duplicate Flow');
    return t('Create Flow');
  });

  const countryName = computed(() => getCountryName(form.value.countryId ?? 0));

  const ruleIndex = computed(() => form.value.rules.indexOf(active_rule.value as never));

  const pathIndex = computed(() => {
    if (!active_rule.value) return -1;
    const rule = active_rule.value as { paths: unknown[] };
    return rule.paths.indexOf(active_path.value);
  });

  const allWorkspaceIds = computed(() => [
    ...new Set([form.value.workspaceId, ...workspaceApi.allPublicWorkspacesIds.value]),
  ]);

  const disabledWorkspace = computed(
    () => Boolean(getData().createFromConvertToFlow) || edit.value,
  );

  const errorsDict = computed(() => fieldErrors.value as Record<string, string[]>);

  const errors = computed(() => translateFlowErrorsRaw(errorsRaw.value, errorMessage.value));

  const isActionsDisabled = computed(() => {
    if (!edit.value) return false;
    return Boolean(
      aclReadOnly({
        workspaceId: form.value.workspaceId,
        id: flow_id.value,
      }),
    );
  });

  const itemActions = computed(() => ({
    cancel: {
      key: 'cancel',
      title: t('Cancel'),
      click: () => checkClose(),
    },
    save: {
      key: 'save',
      title: t('Save'),
      disabled: isActionsDisabled.value,
      click: () => submit(),
    },
  }));

  const successText = computed(() =>
    edit.value ? t('Flow has been changed') : t('Flow has been saved'),
  );

  const countryCondition = ({
    form: flowForm,
    item,
    id,
  }: {
    form: { countryId?: number };
    item: { countryId?: number; id?: string };
    id?: string;
  }) => {
    const countryId = flowForm.countryId;
    return (
      countryId === 0
      || item.countryId === 0
      || countryId === item.countryId
      || Boolean(id && item.id === id)
    );
  };

  const getCountryName = (countryId: number) => {
    const country = store.countryListPlusGlobal.find(item => item.id === countryId);
    if (country) return country.name;
    return t('Global');
  };

  const getCurrentHash = () => md5(JSON.stringify(cloneDeep(toRaw(form.value))));

  const clearSubmitErrors = () => {
    errorsRaw.value = null;
    errorMessage.value = '';
    fieldErrors.value = {};
    errorsControlActive.value = false;
  };

  const applySubmitErrors = (error: unknown) => {
    const parsed = applyFlowSubmitErrorsFromResponse(error);
    if (!parsed) return;
    errorsRaw.value = parsed.errorsRaw;
    errorMessage.value = parsed.errorMessage;
    fieldErrors.value = parsed.fieldErrors;
    errorsControlActive.value = true;
  };

  const setOfferOptions = async () => {
    const { uploadedForWorkspace, uploadedForCountryId, isAllInCache, data, isUploaded } =
      store.cache.offers;

    if (isUploaded && isAllInCache) {
      offerOptions.value = data.filter(
        item =>
          (workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
            || item.workspaceId === form.value.workspaceId)
          && countryCondition({ form: form.value, item }),
      );
      return;
    }

    if (
      isUploaded
      && !isAllInCache
      && uploadedForWorkspace === form.value.workspaceId
      && uploadedForCountryId === form.value.countryId
    ) {
      offerOptions.value = [...data];
      return;
    }

    if (
      form.value.workspaceId === defaultWorkspace.value.id
      && form.value.countryId === 0
    ) {
      offerOptions.value = (await updateOffersStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
        countryId: 0,
      })) as Offer[];
      return;
    }

    offerOptions.value = (await getOffersListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: ['id', 'name', 'countryId', 'affiliateNetworkId', 'affiliateNetworkName', 'countryName', 'url'],
      workspaceId: form.value.workspaceId,
      ...(form.value.countryId !== 0 && { countryId: form.value.countryId }),
    })) as Offer[];
  };

  const setLandingOptions = async () => {
    const { uploadedForWorkspace, uploadedForCountryId, isAllInCache, data, isUploaded } =
      store.cache.landings;

    if (isUploaded && isAllInCache) {
      landingOptions.value = data.filter(
        item =>
          (workspaceApi.allPublicWorkspacesIds.value.includes(item.workspaceId)
            || item.workspaceId === form.value.workspaceId)
          && countryCondition({ form: form.value, item }),
      );
      return;
    }

    if (
      isUploaded
      && !isAllInCache
      && uploadedForWorkspace === form.value.workspaceId
      && uploadedForCountryId === form.value.countryId
    ) {
      landingOptions.value = [...data];
      return;
    }

    if (
      form.value.workspaceId === defaultWorkspace.value.id
      && form.value.countryId === 0
    ) {
      landingOptions.value = (await updateLandingsStoreCacheApiRequest({
        workspaceId: defaultWorkspace.value.id,
        countryId: 0,
      })) as Landing[];
      return;
    }

    landingOptions.value = (await getLandingsListApiRequest({
      status: 'active',
      limit: '200',
      orderBy: 'updated',
      orderDirection: 'desc',
      columns: ['name', 'nameFull', 'id', 'countryId', 'workspaceId'],
      workspaceId: form.value.workspaceId,
      ...(form.value.countryId !== 0 && { countryId: form.value.countryId }),
    })) as Landing[];
  };

  const loadCreateForm = async () => {
    const data = getData();
    form.value = {
      ...defaultForm(),
      workspaceId: defaultWorkspace.value.id,
      ...(data.form || {}),
    } as FlowForm;
  };

  const loadFlowFromApi = async (id: string) => {
    form.value = (await getFlowApiRequest(id)) as FlowForm;
  };

  const initializeDialog = () => {
    show.value = true;
    oldHash.value = getCurrentHash();
    // Re-baseline after UI mounts — selects can normalize values on first paint
    helpers.afterBrowserRedraw(() => {
      flowNavigationRef.value?.openNavigation();
      flowNavigationRef.value?.selectFlowSettings();
      nextTick(() => {
        oldHash.value = getCurrentHash();
      });
    });
  };

  const loadData = async () => {
    view.value = 'settings';
    form.value = defaultForm();

    if (!store.redirectDomains.isUploaded) {
      await populateRedirectDomainsStoreCacheApiRequest();
    }

    if (!store.cache.landings.isUploaded) {
      await populateLandingsStoreCacheApiRequest();
    }

    if (!store.cache.offers.isUploaded) {
      await populateOffersStoreCacheApiRequest();
    }

    if (!store.cache.tags.flows.isUploaded) {
      await populateTagsStoreCacheApiRequest({ type: 'flows' });
    }

    if (mode === 'edit') {
      edit.value = true;
      const data = getData();
      flow_id.value = data.id || (store.getTableSelectedItem().id as string);
      await loadFlowFromApi(flow_id.value);
      initializeDialog();
      return;
    }

    if (mode === 'duplicate') {
      const sourceId = store.getTableSelectedItem().id as string;
      await loadFlowFromApi(sourceId);
      form.value.name = `${form.value.name} (${t('____x.copy')})`;
      initializeDialog();
      return;
    }

    await loadCreateForm();
    initializeDialog();
  };

  const processForm = (source: FlowForm) => {
    const preparedForm = { ...source };
    preparedForm.tags = preparedForm?.tags?.map(item =>
      item && typeof item === 'object' && 'id' in item && item.id ? item.name : item,
    ) as FlowForm['tags'];
    return preparedForm;
  };

  const trackAnalytics = (event: string) => {
    const rules =
      form.value.rules.reduce((accumulator: string[], rule) => {
        Object.keys(rule.conditions || {}).forEach(condition => accumulator.push(condition));
        return accumulator;
      }, []) || [];

    trackMixpanel(event, {
      name: form.value.name,
      rules,
    });
  };

  const close = () => {
    $emitter.off('flow-delete-offer', onBusFlowDeleteOffer);
    $emitter.off('flow-delete-landing', onBusFlowDeleteLanding);
    $emitter.off('loading-add-dictionary', onSaveDictionary);
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash() && !isActionsDisabled.value) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const handleChangeRule = (rule: unknown) => {
    active_rule.value = rule;
  };

  const addPath = (rule: { paths: unknown[] }, callback: (path: unknown, currentRule: unknown) => void) => {
    rule.paths.push({
      name: `Path ${rule.paths.length + 1}`,
      landings: [],
      offers: [],
      status: 'active',
      weight: 100,
      directLinking: false,
    });
    callback(rule.paths[rule.paths.length - 1], rule);
  };

  const deletePath = (rule: { paths: unknown[] }, path: unknown) => {
    const flowNavigation = flowNavigationRef.value;
    if (!flowNavigation) return;

    const pathIndex = rule.paths.indexOf(path);
    const ruleIndexValue = form.value.rules.indexOf(rule as never);

    if (errorsRaw.value) {
      const pathErrors = flowNavigation.processPathError(ruleIndexValue, pathIndex);
      errorsRaw.value = flowNavigation.fixPathErrorIndexes(ruleIndexValue, pathIndex, pathErrors);
    }

    rule.paths.splice(pathIndex, 1);

    if (rule.paths.length) {
      flowNavigation.setPath(
        rule.paths[pathIndex] || rule.paths[rule.paths.length - 1],
        rule,
      );
      emitRefresh();
    } else {
      flowNavigation.setRule(rule);
    }
  };

  const duplicatePath = (rule: { paths: unknown[]; name?: string }, path: Record<string, unknown>) => {
    const pathIndex = rule.paths.indexOf(path) + 1;
    const newPath = {
      ...cloneDeep(path),
      name: `${path.name} ${t('Duplicate')}`,
    };

    rule.paths.splice(pathIndex, 0, newPath);
    flowNavigationRef.value?.setPath(rule.paths[pathIndex], rule);
  };

  const addRule = () => {
    form.value.rules.push({
      name: '',
      default: false,
      conditions: {},
      status: 'active',
      paths: [],
      active: true,
      logicalRelation: 'and',
    });

    const rule = form.value.rules[form.value.rules.length - 1];

    nextTick(() => {
      addPath(rule, () => {
        flowNavigationRef.value?.setRule(rule);
      });
    });
  };

  const duplicateRule = (rule: Record<string, unknown>) => {
    const ruleIndexValue = form.value.rules.indexOf(rule as never) + 1;
    const newRule = {
      ...cloneDeep(rule),
      name: `${rule.name} ${t('Duplicate')}`,
    };

    form.value.rules.splice(ruleIndexValue, 0, newRule as never);
    flowNavigationRef.value?.setRule(newRule);
  };

  const deleteRule = (rule: unknown) => {
    const flowNavigation = flowNavigationRef.value;
    if (!flowNavigation) return;

    const ruleIndexValue = form.value.rules.indexOf(rule as never);

    if (errorsRaw.value) {
      const ruleErrors = flowNavigation.processRuleError(ruleIndexValue);
      errorsRaw.value = flowNavigation.fixRuleErrorIndexes(ruleIndexValue, ruleErrors);
    }

    form.value.rules.splice(ruleIndexValue, 1);
    flowNavigation.setRule(
      form.value.rules[ruleIndexValue] || form.value.rules[form.value.rules.length - 1],
    );
  };

  const onChangeWorkspace = (value: string) => {
    // Ignore empty/same-value emits (Vuetify 3 select can fire change on mount)
    if (!value || value === form.value.workspaceId) return;

    const { name, tags, countryId } = form.value;
    form.value = {
      ...defaultForm(),
      workspaceId: value,
      name,
      tags,
      countryId,
    } as FlowForm;
    setLandingOptions();
    setOfferOptions();
  };

  const onChangeCountry = () => {
    setLandingOptions();
    setOfferOptions();
  };

  const onBusFlowDeleteOffer = (
    ruleIndexValue: number,
    pathIndexValue: number,
    offerIndex: number,
  ) => {
    if (!errorsRaw.value) return;
    const flowNavigation = flowNavigationRef.value;
    if (!flowNavigation) return;

    const offerErrors = flowNavigation.processOfferError(
      ruleIndexValue,
      pathIndexValue,
      offerIndex,
    );
    errorsRaw.value = flowNavigation.fixOfferErrorIndexes(
      ruleIndexValue,
      pathIndexValue,
      offerIndex,
      offerErrors,
    );
  };

  const onBusFlowDeleteLanding = (
    ruleIndexValue: number,
    pathIndexValue: number,
    landingIndex: number,
  ) => {
    if (!errorsRaw.value) return;
    const flowNavigation = flowNavigationRef.value;
    if (!flowNavigation) return;

    const landingErrors = flowNavigation.processLandingError(
      ruleIndexValue,
      pathIndexValue,
      landingIndex,
    );
    errorsRaw.value = flowNavigation.fixLandingErrorIndexes(
      ruleIndexValue,
      pathIndexValue,
      landingIndex,
      landingErrors,
    );
  };

  const onSaveDictionary = (isLoading: boolean) => {
    isLoadingDictionary.value = isLoading;
  };

  const submit = async () => {
    if (isLoadingDictionary.value) return;

    store.setLoading(true);
    submitLoading.value = true;
    clearSubmitErrors();

    try {
      const data = getData();

      if (mode === 'edit') {
        const result = await putUpdateFlowApiRequest(flow_id.value, processForm(form.value));
        const { id } = result;
        reportUtilsApi.updateName('flows', id as string);
        trackAnalytics('flow_edit');
        data.onConfirm?.(result);
      } else {
        const result = await postCreateFlowApiRequest(processForm(form.value));
        flow_id.value = result.id as string;
        trackAnalytics('flow_create');
        data.onConfirm?.(result);
      }

      await refreshUserProfileFromApi();
      $emitter.emit('toast-success', successText.value);
      $emitter.emit('refresh-report');
      close();
    } catch (error) {
      applySubmitErrors(error);
    } finally {
      store.setLoading(false);
      submitLoading.value = false;
    }
  };

  watch(
    form,
    () => {
      emitRefresh();
    },
    { deep: true },
  );

  onMounted(async () => {
    $emitter.on('flow-delete-offer', onBusFlowDeleteOffer);
    $emitter.on('flow-delete-landing', onBusFlowDeleteLanding);
    $emitter.on('loading-add-dictionary', onSaveDictionary);

    store.setLoading(true);
    try {
      await loadData();
      await setLandingOptions();
      await setOfferOptions();
    } finally {
      store.setLoading(false);
    }
  });

  onUnmounted(() => {
    $emitter.off('flow-delete-offer', onBusFlowDeleteOffer);
    $emitter.off('flow-delete-landing', onBusFlowDeleteLanding);
    $emitter.off('loading-add-dictionary', onSaveDictionary);
  });

  return {
    store,
    t,
    show,
    view,
    form,
    active_path,
    active_rule,
    rule_placeholder,
    active_path_errors,
    form_errors,
    submitLoading,
    offerOptions,
    landingOptions,
    errorsRaw,
    errors,
    errorsControlActive,
    errorsDict,
    flowNavigationRef,
    workspacesList,
    header,
    modalTitle,
    countryName,
    ruleIndex,
    pathIndex,
    allWorkspaceIds,
    disabledWorkspace,
    isActionsDisabled,
    itemActions,
    checkClose,
    handleChangeRule,
    addPath,
    deletePath,
    duplicatePath,
    addRule,
    duplicateRule,
    deleteRule,
    onChangeWorkspace,
    onChangeCountry,
    submit,
  };
};
